<template>
  <Teleport to="body">
    <div
      class="new-ui ui-toast-host"
      :class="[`ui-toast-host--${vertical}`, `ui-toast-host--${horizontal}`]"
      :style="insetStyle"
      aria-live="polite"
      @keydown.esc="closeNewest"
    >
      <TransitionGroup
        name="ui-toast"
        tag="div"
        class="ui-toast-stack"
        @before-leave="pinLeavingToast"
      >
        <UiToast
          v-for="toast in toasts"
          :key="toast.id"
          :toast="toast"
          @close="closeToast(toast.id)"
          @action="runAction(toast)"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import UiToast from "@/components/newUi/toast/UiToast.vue";
import {
  createToastItem,
  DEFAULT_PUBLIC_PLACEMENT,
  decideStackChange,
  findDuplicateToast,
  getPlacementParts,
  normalizePlacement,
  type ToastItem,
} from "@/components/newUi/toast/toastModel";
import { useToastInsets } from "@/components/newUi/toast/useToastInsets";
import type { Notification } from "@/types";

const store = useStore();
const router = useRouter();

const toasts = ref<ToastItem[]>([]);

const notifications = computed<Notification[]>(() => store.getters["notifications/notifications"]);

const hasLoadedUser = computed(
  () => store.getters["auth/isAuthenticated"] === true && Boolean(store.getters["auth/user"]),
);

const placement = computed(() =>
  hasLoadedUser.value
    ? normalizePlacement(
        store.getters["notifications/placementPreview"] ??
          store.getters["notifications/notificationPlacement"],
      )
    : DEFAULT_PUBLIC_PLACEMENT,
);

const parts = computed(() => getPlacementParts(placement.value));

const vertical = computed(() => parts.value.vertical);

const horizontal = computed(() => parts.value.horizontal);

const { insetStyle } = useToastInsets(vertical, horizontal, () => toasts.value.length);

const knownNotifications = new Map<number, Notification>();

const removeFromStore = (id: number): void => {
  const notification = knownNotifications.get(id);

  if (notification) {
    store.commit("notifications/clearNotification", notification);
  }
};

const closeToast = (id: number): void => {
  toasts.value = toasts.value.filter((toast) => toast.id !== id);
  removeFromStore(id);
  knownNotifications.delete(id);
};

const pinLeavingToast = (element: Element): void => {
  const toast = element as HTMLElement;

  toast.style.top = `${toast.offsetTop}px`;
  toast.style.left = `${toast.offsetLeft}px`;
  toast.style.width = `${toast.offsetWidth}px`;
};

const closeNewest = (): void => {
  const newest = toasts.value[toasts.value.length - 1];

  if (newest) {
    closeToast(newest.id);
  }
};

const runAction = (toast: ToastItem): void => {
  const { action } = toast;

  if (action?.handler) {
    action.handler();
  }

  if (action?.link) {
    router.push(action.link);
  }
};

const intake = (notification: Notification): void => {
  if (!notification || knownNotifications.has(notification.id as number)) {
    return;
  }

  const now = Date.now();
  const incoming = createToastItem(notification, now);
  const duplicate = findDuplicateToast(toasts.value, incoming, now);

  if (duplicate) {
    duplicate.count += 1;
    duplicate.touchedAt = now;
    duplicate.restartKey += 1;
    store.commit("notifications/clearNotification", notification);

    return;
  }

  const decision = decideStackChange(toasts.value, incoming);

  if (decision.action === "drop") {
    store.commit("notifications/clearNotification", notification);

    return;
  }

  if (decision.evictedId !== null) {
    closeToast(decision.evictedId);
  }

  knownNotifications.set(incoming.id, notification);
  toasts.value = [...toasts.value, incoming];
};

watch(
  () => [...notifications.value],
  (current) => {
    [...current].forEach(intake);

    const currentIds = new Set(current.map((notification) => notification?.id));

    toasts.value = toasts.value.filter((toast) => {
      const isStillInStore = currentIds.has(toast.id);

      if (!isStillInStore) {
        knownNotifications.delete(toast.id);
      }

      return isStillInStore;
    });
  },
  { immediate: true },
);

watch(
  () => toasts.value.length,
  (length) => {
    if (length === 0 && store.getters["notifications/placementPreview"] !== null) {
      store.commit("notifications/setPlacementPreview", null);
    }
  },
);
</script>
